import { useEffect, useState, type FormEvent } from "react";
import { Lock, X } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { useCoffreSuiteStatus } from "@/hooks/useCoffreSuiteStatus";
import { getSuiteAppUrl } from "@/lib/suite/suiteApiFetch";
import {
  CoffreUnlockDenied,
  coffreUnlockClosed,
  MAIL_COFFRE_UNLOCKED_EVENT,
  onCoffreUnlockAsk,
  unlockCoffreVault,
} from "@/lib/suite/coffreUnlock";

export function MailCoffreUnlockHost() {
  const { refresh } = useCoffreSuiteStatus();
  const [open, setOpen] = useState(false);

  useEffect(() => onCoffreUnlockAsk(() => setOpen(true)), []);

  function close() {
    coffreUnlockClosed();
    setOpen(false);
  }

  return (
    <MailModalOverlay open={open} onClose={close} labelledBy="mail-coffre-unlock-title">
      <MailCoffreUnlockForm
        onClose={close}
        onUnlocked={async () => {
          coffreUnlockClosed();
          setOpen(false);
          await refresh();
          window.dispatchEvent(new Event(MAIL_COFFRE_UNLOCKED_EVENT));
        }}
      />
    </MailModalOverlay>
  );
}

function MailCoffreUnlockForm({
  onClose,
  onUnlocked,
}: {
  onClose: () => void;
  onUnlocked: () => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState("");
  const [stopped, setStopped] = useState(false);
  const [needsVault, setNeedsVault] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (code.trim() === "" || busy || stopped) return;
    setBusy(true);
    setFail("");
    setNeedsVault(false);
    try {
      await unlockCoffreVault(code);
      setCode("");
      await onUnlocked();
    } catch (reason: unknown) {
      setFail(reason instanceof Error ? reason.message : "Mot de passe maître refusé.");
      setCode("");
      if (reason instanceof CoffreUnlockDenied && reason.code === "coffre_suite_locked") {
        setStopped(true);
      }
      if (reason instanceof CoffreUnlockDenied && reason.code === "vault_not_initialized") {
        setNeedsVault(true);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="px-5 py-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--mail-muted)]">
            Coffre
          </p>
          <h2
            id="mail-coffre-unlock-title"
            className="mt-1 flex items-center gap-2 text-lg font-semibold text-[var(--mail-text)]"
          >
            <Lock className="h-4 w-4" aria-hidden />
            Mot de passe maître
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="compose-drawer-icon-btn"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[var(--mail-muted)]">
        Saisissez votre mot de passe maître pour ouvrir le Coffre depuis Mail. Il n’est pas
        enregistré ici.
      </p>
      <input
        type="password"
        name="coffre-unlock-pass"
        autoComplete="off"
        autoFocus
        value={code}
        disabled={busy || stopped}
        onChange={(event) => setCode(event.target.value)}
        placeholder="Mot de passe maître"
        className="mt-4 w-full rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] outline-none focus:border-[var(--mail-accent)]"
      />
      {fail ? (
        <p className="mt-2 text-sm text-[var(--mail-danger-text)]" role="alert">
          {fail}{" "}
          {needsVault ? (
            <a
              href={getSuiteAppUrl("coffre/dist/index.html")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--mail-accent)] underline"
            >
              Ouvrir le Coffre
            </a>
          ) : null}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="mail-btn-ghost rounded-full px-4 py-1.5 text-sm">
          Annuler
        </button>
        <button
          type="submit"
          disabled={busy || stopped || code.trim() === ""}
          className="mail-btn-primary rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? "Vérification…" : "Accéder au Coffre"}
        </button>
      </div>
    </form>
  );
}
